import { useState, useEffect, useContext } from "react";
import { useParams } from "react-router-dom";

import {
  Box,
  TextField,
  FormControl,
  Grid,
  InputLabel,
  Typography,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Snackbar,
  Alert,
  FormHelperText,
  OutlinedInput,
} from "@mui/material";

import { UserContext } from "../../context/userContext";
import { UserLoginInfo } from "../../types/context";
import { appLogger, Http } from "../../utils";
import { withSnackbar } from "../../utils/common/SnackbarHoc";

import { useNavigate } from "react-router-dom";

import moment from "moment";

const initialEnabledFeaturesValues = {
  enableAnalytics: false,
  enableAppstore: false,
  enableBugReport: false,
  enableChatbot: false,
  enableCorrelation: false,
  enableFeatureRequest: false,
  enableFeedback: false,
  enableGenerativeAI: false,
  enableJira: false,
  enableMailReply: false,
  enablePlaystore: false,
  enableZendesk: false,
};

const initialOrgValues = {
  name: "",
  status: "Trial",
  logLevel: "info",
};

const logLevelInitialValues = [
  {
    label: "Info",
    value: "info",
  },
  {
    label: "Warn",
    value: "warn",
  },
  {
    label: "Error",
    value: "error",
  },
];

const statusInitialValues = [
  {
    label: "Active",
    value: "Active",
  },
  {
    label: "Trial",
    value: "Trial",
  },
];

const OrganizationDetails = (props: any) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { userInfo } = useContext(UserContext) as UserLoginInfo;

  const [org, setOrg] = useState<any>({});
  const [enabledFeatures, setEnabledFeatures] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState<any>({});

  const handleValidation = () => {
    const { name } = org || {};
    if (!name) {
      setErrors({ name: "Organization name is required" });
      return false;
    }

    return true;
  };

  const handleFetchOrganization = () => {
    Http.get({
      url: `/api/v2/organizations/${id}`,
      state: userInfo,
    })
      .then((response: any) => {
        const tempOrg = { ...response.organizationDetails };
        const { enabledFeatures } = tempOrg;
        setOrg(tempOrg);
        setEnabledFeatures(enabledFeatures);
      })
      .catch((error) => {
        appLogger.error({
          errObj: {
            method: "useEffect",
            component: "header",
            fileName: "index.tsx",
            err: error,
          },
        });
      });
  };

  useEffect(() => {
    if (id !== "create") {
      handleFetchOrganization();
    } else {
      setOrg({
        ...initialOrgValues,
        createdBy: userInfo?.userDetails?.createdBy,
      });
      setEnabledFeatures(initialEnabledFeaturesValues);
    }
  }, [id]);

  const handleChange = (e: any) => {
    setErrors({});
    const { name, value } = e.target;
    if (name === "name") {
      const newValue = value.replace(/\s/g, "");
      setOrg({ ...org, [name]: newValue });
    } else {
      setOrg({ ...org, [name]: value });
    }
  };

  const handleCheckbox = (e: any) => {
    const key = e.target.name;
    setEnabledFeatures({ ...enabledFeatures, [key]: !enabledFeatures[key] });
  };

  const handleConvertToTitleCase = (str: string) => {
    return str.replace(/([A-Z])/g, " $1").replace(/^./, function (s) {
      return s.toUpperCase();
    });
  };

  const handleUpdateOrganization = async () => {
    setLoading(true);
    try {
      const data = {
        ...org,
        enabledFeatures,
      };

      delete data?.parent;
      delete data?.searchKey;
      delete data?._id;
      delete data?.createdBy;
      delete data?.createdOn;
      delete data?.lastModifiedBy;
      delete data?.lastModifiedOn;

      const res = await Http.put({
        url: `/api/v2/organizations/`,
        body: { ...data },
        state: userInfo,
      });

      if (res) {
        props.snackbarShowMessage(
          "Successfully updated organization",
          "success",
          3000
        );

        localStorage.setItem(
          "userenabledFeatures",
          JSON.stringify(enabledFeatures)
        );

        setTimeout(() => {
          navigate("/manage-organizations");
        }, 1000);
      }
    } catch (err) {
      appLogger.error({
        errObj: {
          method: "handleUpdateOrganization",
          component: "OrganizationDetails",
          fileName: "OrganizationDetails.tsx",
          err: err,
        },
      });
      setLoading(false);
      props.snackbarShowMessage(
        "Oops something went wrong, please try again",
        "error",
        3000
      );
    }
  };

  const handleCreateOrganization = async () => {
    setLoading(true);
    try {
      const data = {
        ...org,
        enabledFeatures,
      };

      delete data?.createdBy;

      const res: any = await Http.post({
        url: `/api/v2/organizations/`,
        state: userInfo,
        body: data,
      });
      if (res?.message) {
        if (res?.message === "Organization already exists") {
          props.snackbarShowMessage(res?.message, "error", 3000);
          setLoading(false);
        } else {
          props.snackbarShowMessage(
            "Successfully created organization",
            "success",
            3000
          );

          setTimeout(() => {
            navigate("/manage-organizations");
          }, 1000);
        }
      }
    } catch (err) {
      appLogger.error({
        errObj: {
          method: "handleUpdateOrganization",
          component: "OrganizationDetails",
          fileName: "OrganizationDetails.tsx",
          err: err,
        },
      });
      setLoading(false);
      props.snackbarShowMessage(
        "Oops something went wrong, please try again",
        "error",
        3000
      );
    }
  };

  const handleSubmit = () => {
    if (handleValidation()) {
      if (id !== "create") {
        handleUpdateOrganization();
      } else {
        handleCreateOrganization();
      }
    }
  };

  return (
    <Grid
      container
      p={3}
      sx={{
        border: "1px solid gray",
        borderRadius: "8px",
      }}
      mt="40px"
      spacing={2}
    >
      <Grid item md={6} xs={12} mb="10px">
        {id !== "create" && (
          <Typography
            sx={{
              fontSize: "14px",
            }}
          >
            Last modified by{" "}
            <span style={{ fontWeight: 700 }}>{org?.lastModifiedBy}</span>
          </Typography>
        )}

        {id === "create" && (
          <Typography
            sx={{
              fontSize: "14px",
            }}
          >
            Kindly fill in the required fields
          </Typography>
        )}
      </Grid>
      <Grid item md={6} xs={12} mb="10px" textAlign="right">
        <Typography
          sx={{
            fontSize: "14px",
          }}
        >
          Created on{" "}
          {id !== "create" && (
            <span style={{ fontWeight: 700 }}>
              {moment(org?.createdOn).format("MM/DD/YYYY")}
            </span>
          )}
          {id === "create" && (
            <span style={{ fontWeight: 700 }}>
              {moment().format("MM/DD/YYYY")}
            </span>
          )}
        </Typography>
      </Grid>
      <Grid item md={6} xs={12}>
        <TextField
          id="orgname"
          name="name"
          className="date-picker custom-select"
          label="Name"
          value={org.name}
          onChange={handleChange}
          sx={{
            "& input": {
              textTransform: "unset!important",
            },
          }}
          InputLabelProps={{
            shrink: true,
            required: true,
          }}
          required
        />
        {errors?.name && (
          <FormHelperText
            sx={{
              marginLeft: "13px",
              color: "#d74141",
            }}
          >
            {errors?.name}
          </FormHelperText>
        )}
      </Grid>
      <Grid item md={6} xs={12}>
        <TextField
          name="createdBy"
          className="date-picker custom-select"
          label="Created by"
          value={org?.createdBy}
          onChange={handleChange}
          sx={{
            "& input": {
              textTransform: "unset!important",
            },
          }}
          InputLabelProps={{
            shrink: true,
          }}
          disabled
        />
      </Grid>

      <Grid item md={6} xs={12}>
        <FormControl className="custom-select" fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            name="status"
            value={org?.status || ""}
            onChange={handleChange}
          >
            {statusInitialValues.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item md={6} xs={12}>
        <FormControl className="custom-select" fullWidth>
          <InputLabel>Log level</InputLabel>
          <Select
            name="logLevel"
            value={org?.logLevel || ""}
            onChange={handleChange}
          >
            {logLevelInitialValues.map((level) => (
              <MenuItem key={level.value} value={level.value}>
                {level.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid container item xs={12} mt="20px">
        <Grid item xs={12}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            Enabled Features
          </Typography>
        </Grid>
        {Object.keys(enabledFeatures).length > 0 && (
          <Grid container item xs={12} mt="10px">
            {Object.keys(enabledFeatures).map((key) => {
              return (
                <Grid item xs={12} mb="5px">
                  <FormControlLabel
                    control={
                      <Checkbox
                        name={key}
                        checked={enabledFeatures[key]}
                        onChange={handleCheckbox}
                        color="primary"
                        sx={{
                          padding: "0 5px",
                        }}
                      />
                    }
                    label={handleConvertToTitleCase(key)}
                  />
                </Grid>
              );
            })}
          </Grid>
        )}
      </Grid>

      <Grid container item xs={12} justifyContent="end" gap="15px" mt="30px">
        <Button
          variant="outlined"
          onClick={() => {
            navigate(-1);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={Object.keys(errors).length > 0 || loading}
          onClick={handleSubmit}
        >
          Save
        </Button>
      </Grid>
    </Grid>
  );
};

export default withSnackbar(OrganizationDetails);
